import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useInstall } from "@/lib/install";
import { useAuth } from "@/context/AuthContext";

function HowTo({ open, onClose, ios }) {
  const steps = ios
    ? [[Share, "Tap the Share button at the bottom of Safari"], [PlusSquare, "Scroll down and tap “Add to Home Screen”"], [Smartphone, "Tap “Add” — VacaVibe appears on your home screen"]]
    : [[Download, "Open your browser menu (⋮)"], [PlusSquare, "Tap “Install app” or “Add to Home screen”"], [Smartphone, "Confirm — VacaVibe appears on your home screen"]];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="install-instructions" className="bg-[#151C2C] border-[#26334D] text-white max-w-sm">
        <DialogHeader className="text-left">
          <img src="/icon-192.png" alt="VacaVibe" className="h-16 w-16 rounded-2xl mb-2" />
          <DialogTitle className="text-xl font-bold">Add VacaVibe to your home screen</DialogTitle>
          <DialogDescription className="text-slate-400">Opens full-screen like a regular app — no App Store needed.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 mt-2">
          {steps.map(([Icon, text]) => (
            <li key={text} className="flex items-center gap-3 text-sm">
              <span className="h-9 w-9 rounded-xl bg-[#FF3B6B]/15 text-[#FF3B6B] grid place-items-center shrink-0"><Icon className="h-4 w-4" /></span>{text}
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
}

export function InstallButton({ className = "", testid = "install-app-button", label = "Get the app" }) {
  const { installed, canPrompt, ios, install } = useInstall();
  const [help, setHelp] = useState(false);
  if (installed) return null;
  const click = async () => {
    if (canPrompt) { if (await install()) toast.success("VacaVibe is on your home screen!"); }
    else setHelp(true);
  };
  return (
    <>
      <Button data-testid={testid} onClick={click} className={`rounded-full bg-white text-[#0B0F19] hover:bg-slate-200 font-semibold ${className}`}>
        <Smartphone className="h-4 w-4 mr-1.5" /> {label}
      </Button>
      <HowTo open={help} onClose={() => setHelp(false)} ios={ios} />
    </>
  );
}

const VISITS_KEY = "vv_visits";
function countVisit() {
  if (!sessionStorage.getItem("vv_session")) {
    sessionStorage.setItem("vv_session", "1");
    localStorage.setItem(VISITS_KEY, String(Number(localStorage.getItem(VISITS_KEY) || 0) + 1));
  }
  return Number(localStorage.getItem(VISITS_KEY));
}

function useNearTop() {
  const [top, setTop] = useState(() => window.scrollY < 200);
  useEffect(() => {
    const f = () => setTop(window.scrollY < 200);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return top;
}

export function InstallBanner() {
  const { installed } = useInstall();
  const { user } = useAuth();
  const nearTop = useNearTop();
  const [hidden, setHidden] = useState(true);
  useEffect(() => { setHidden(localStorage.getItem("vv_install_dismissed") === "1" || countVisit() < 2); }, []);
  if (installed || hidden) return null;
  const dismiss = () => { localStorage.setItem("vv_install_dismissed", "1"); setHidden(true); };
  return (
    <div data-testid="install-banner" aria-hidden={!nearTop}
      className={`md:hidden fixed inset-x-3 z-30 rounded-2xl bg-[#151C2C]/95 backdrop-blur-xl border border-[#26334D] p-2.5 flex items-center gap-3 shadow-2xl transition-[transform,opacity] duration-300 ${user ? "bottom-[calc(4.75rem+env(safe-area-inset-bottom))]" : "bottom-[calc(0.75rem+env(safe-area-inset-bottom))]"} ${nearTop ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0 pointer-events-none"}`}>
      <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-xl" />
      <p className="flex-1 min-w-0 text-sm font-semibold leading-tight">VacaVibe on your phone<span className="block text-xs font-normal text-slate-400">Add it to your home screen</span></p>
      <InstallButton testid="install-banner-button" label="Add" className="h-9 px-4" />
      <button data-testid="install-banner-dismiss" onClick={dismiss} aria-label="Dismiss" className="h-10 w-10 -mr-1 grid place-items-center rounded-full text-slate-500 active:bg-white/10"><X className="h-4 w-4" /></button>
    </div>
  );
}
